import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
// routes
import { paths } from 'src/routes/paths';
// ransomware global scope (persists filter/entity context across the module)
import { RansomwareScopeProvider } from 'src/sections/threat-intelligence/ransomware/analytics-filters';

// ----------------------------------------------------------------------

const TelegramFeedsPage = lazy(() => import('src/pages/threat-intelligence/telegram-feeds'));
const SourceManagementPage = lazy(
  () => import('src/pages/threat-intelligence/source-management')
);

// ransomware intelligence
const RansomwareOverviewPage = lazy(
  () => import('src/pages/threat-intelligence/ransomware/overview')
);
const RansomwareVictimsPage = lazy(
  () => import('src/pages/threat-intelligence/ransomware/victims')
);
const RansomwareSectorsPage = lazy(
  () => import('src/pages/threat-intelligence/ransomware/sectors')
);
const RansomwareGeoPage = lazy(() => import('src/pages/threat-intelligence/ransomware/geo'));
const RansomwareGroupsPage = lazy(
  () => import('src/pages/threat-intelligence/ransomware/groups')
);
const RansomwareGroupProfilePage = lazy(
  () => import('src/pages/threat-intelligence/ransomware/group-profile')
);
const RansomwareMitrePage = lazy(() => import('src/pages/threat-intelligence/ransomware/mitre'));
const RansomwareSectorProfilePage = lazy(
  () => import('src/pages/threat-intelligence/ransomware/sector-profile')
);
const RansomwareCountryProfilePage = lazy(
  () => import('src/pages/threat-intelligence/ransomware/country-profile')
);
const RansomwareGraphPage = lazy(() => import('src/pages/threat-intelligence/ransomware/graph'));
const RansomwareCampaignsPage = lazy(
  () => import('src/pages/threat-intelligence/ransomware/campaigns')
);
const RansomwareThreatIntelPage = lazy(
  () => import('src/pages/threat-intelligence/ransomware/threat-intel')
);
const RansomwareCveProfilePage = lazy(
  () => import('src/pages/threat-intelligence/ransomware/cve-profile')
);
const RansomwareSearchPage = lazy(() => import('src/pages/threat-intelligence/ransomware/search'));
// watchlist disabled for now — feature files kept on disk, just unwired
// const RansomwareWatchlistPage = lazy(
//   () => import('src/pages/threat-intelligence/ransomware/watchlist')
// );

// ----------------------------------------------------------------------

export const threatIntelligenceRoutes = [
  {
    path: 'threatIntelligence',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      // No landing page — the module opens on its first sub-module
      { element: <Navigate to={paths.threatIntelligence.telegramFeeds} replace />, index: true },
      { path: 'telegram-feeds', element: <TelegramFeedsPage /> },
      { path: 'source-management', element: <SourceManagementPage /> },
      {
        path: 'ransomware',
        element: (
          <RansomwareScopeProvider>
            <Outlet />
          </RansomwareScopeProvider>
        ),
        children: [
          { element: <RansomwareOverviewPage />, index: true },
          { path: 'victims', element: <RansomwareVictimsPage /> },
          { path: 'sectors', element: <RansomwareSectorsPage /> },
          { path: 'sectors/:name', element: <RansomwareSectorProfilePage /> },
          { path: 'geo', element: <RansomwareGeoPage /> },
          { path: 'geo/:code', element: <RansomwareCountryProfilePage /> },
          { path: 'groups', element: <RansomwareGroupsPage /> },
          { path: 'groups/:name', element: <RansomwareGroupProfilePage /> },
          { path: 'graph', element: <RansomwareGraphPage /> },
          { path: 'campaigns', element: <RansomwareCampaignsPage /> },
          { path: 'threat-intel', element: <RansomwareThreatIntelPage /> },
          { path: 'vulnerabilities/:id', element: <RansomwareCveProfilePage /> },
          { path: 'search', element: <RansomwareSearchPage /> },
          // watchlist disabled for now
          // { path: 'watchlist', element: <RansomwareWatchlistPage /> },
          { path: 'mitre', element: <RansomwareMitrePage /> },
        ],
      },
    ],
  },
];
